/**
 * ModuleSelector - global header module switcher
 *
 * Always renders when modules are present (even a single module).
 * - 0 modules  → renders nothing (loading / unassigned state)
 * - 1 module   → renders nothing (no choice to make)
 * - 2+ modules → full dropdown switcher
 *
 * Design decisions:
 * - Module identity is always visible so users know which context they are in
 * - Placed in the global header between workspace identity and the end actions
 * - Selection triggers optimistic local update + background persistence via onSelect
 * - No inline CSS - styles live alongside this file
 */

import type { ModuleResponse } from '@protoss/shared';
import { ChevronDownIcon, DropdownMenu, IconSize } from '@protoss/ui';
import { useNavigate } from 'react-router-dom';

export interface ModuleSelectorProps {
  /** Ordered list of enabled modules for this workspace */
  modules: ModuleResponse[];
  /** The currently active module key */
  activeModuleKey: string | null;
  /** Called when the user picks a different module */
  onSelect: (moduleKey: string) => void;
}

/**
 * ModuleSelector
 *
 * Placed in the global header.
 * - 0 modules  → null (loading or unassigned)
 * - 1 module   → static label badge (no interaction)
 * - 2+ modules → dropdown switcher
 */
export function ModuleSelector({ modules, activeModuleKey, onSelect }: ModuleSelectorProps) {
  const navigate = useNavigate();
  if (modules.length === 0) {
    return null;
  }

  const activeModule = modules.find((m) => m.key === activeModuleKey) ?? modules[0];

  if (modules.length === 1) {
    return null;
  }

  return (
    <div className="module-selector">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button type="button" className="module-selector__trigger" aria-label="Switch module">
            <span className="module-selector__label">{activeModule?.label ?? 'Module'}</span>
            <ChevronDownIcon
              width={IconSize.sm}
              height={IconSize.sm}
              className="module-selector__caret"
              aria-hidden="true"
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="module-selector__content dropdown-content"
            sideOffset={4}
            align="end"
          >
            {modules.map((mod) => (
              <DropdownMenu.Item
                key={mod.key}
                className={`module-selector__item dropdown-item${mod.key === activeModuleKey ? ' module-selector__item--active' : ''}`}
                onSelect={() => {
                  if (mod.key !== activeModuleKey) {
                    onSelect(mod.key);
                    navigate(mod.defaultRoute);
                  }
                }}
              >
                <span className="module-selector__item-label">{mod.label}</span>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
